import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Facebook, Twitter, Youtube, Instagram, Clock, Zap } from 'lucide-react';
import { cn } from '../lib/utils';
import { useTranslate } from '../context/LanguageContext';
import { fetchBreakingNews } from '../api';

interface TopBarProps {
  className?: string;
  tickerSpeed?: number;
}

export default function TopBar({ className, tickerSpeed = 40 }: TopBarProps) {
  const { language, t } = useTranslate();
  const [dateStr, setDateStr] = useState('');
  const [speed, setSpeed] = useState(tickerSpeed);

  useEffect(() => {
    const isAr = language === 'ar';
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      ...(isAr ? { calendar: 'islamic-sa' } : {})
    };
    try {
      const formatter = new Intl.DateTimeFormat(isAr ? 'ar-SA' : 'en-US', options);
      setDateStr(formatter.format(new Date()));
    } catch (e) {
      // Fallback if islamic-sa is not supported or errors out
      const formatter = new Intl.DateTimeFormat(isAr ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric'});
      setDateStr(formatter.format(new Date()));
    }
  }, [language]);

  const defaultNews = [
    t("نورالدين كوكى: الحوار هو السبيل الوحيد لتجاوز الأزمات السياسية الراهنة"),
    t("تغطية خاصة: الميدان الثوري يشهد تحولات جذرية في الحراك الشعبي"),
    t("جديد المقالات: 'رؤية مستقبلية لبناء الدولة الحديثة' متوفر الآن"),
    t("عاجل: انطلاق سلسلة ندوات فكرية حول المواطنة والحقوق السياسية"),
  ];

  const [breakingNews, setBreakingNews] = useState<string[]>(defaultNews);

  useEffect(() => {
    let active = true;

    const doFetch = () => {
      fetchBreakingNews(language)
        .then((data) => {
          if (active && Array.isArray(data) && data.length > 0) {
            setBreakingNews(data);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch breaking news:", err);
        });
    };

    doFetch(); // fetch immediately on mount / language change

    // Re-fetch every 2 minutes so newly published articles appear in the ticker
    const interval = setInterval(doFetch, 2 * 60 * 1000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [language]);


  return (
    <div 
      className={cn("hidden md:block bg-primary text-white border-b border-white/10 relative z-[60]", className)}
      style={{ paddingTop: '15px', paddingBottom: '15px' }}
    >
      <div className="w-full px-4 lg:px-12 flex items-center">
        
        {/* Classic Date Section - Hidden on small mobile */}
        <div className="hidden md:flex items-center gap-2 border-e border-white/10 pe-6 h-full flex-shrink-0">
          <Clock size={12} className="text-secondary" />
          <span className="text-[11px] font-medium tracking-tight opacity-90 whitespace-nowrap">
            {dateStr}
          </span>
        </div>

        {/* Traditional Ticker Section */}
        <div className="flex-1 flex items-center h-full relative overflow-hidden px-4 md:px-6 group">
          <span className="text-secondary font-black text-[10px] md:text-[11px] ms-2 md:ms-4 flex-shrink-0 flex items-center gap-1.5 md:gap-2 relative z-30 bg-primary pe-2">
            <Zap size={11} className="fill-secondary" />
            <span className="hidden xs:inline">{t('أخبار عاجلة')}</span>
            <span className="xs:hidden uppercase">{t('عاجل')}</span>
          </span>
          
          <div className="flex-1 relative overflow-hidden h-full flex items-center">
            {/* Enhanced Fade Masks */}
            <div className="absolute inset-y-0 left-0 w-12 md:w-24 z-20 pointer-events-none bg-gradient-to-r from-primary via-primary/80 to-transparent" />
            <div className="absolute inset-y-0 right-0 w-12 md:w-24 z-20 pointer-events-none bg-gradient-to-l from-primary via-primary/80 to-transparent" />

            {/* Scrolling Ticker - Directional based on Language */}
            <motion.div 
              className="flex items-center gap-10 md:gap-16 whitespace-nowrap"
              key={`${language}-${speed}`} // Remount on speed or language change for smooth transition reset
              initial={{ x: language === 'ar' ? "-50%" : "0%" }}
              animate={{ x: language === 'ar' ? "0%" : "-50%" }}
              transition={{ 
                duration: speed, 
                repeat: Infinity, 
                ease: "linear" 
              }}
            >
              {[...breakingNews, ...breakingNews].map((news, idx) => (
                <div key={idx} className="flex items-center gap-3 md:gap-4">
                  <span className="w-1 md:w-1.5 h-1 md:h-1.5 rounded-full bg-secondary/60 shadow-[0_0_8px_rgba(255,63,52,0.4)]" />
                  <span className="text-[11px] md:text-[12px] font-medium text-white/90 hover:text-white transition-colors cursor-default whitespace-nowrap">
                    {news}
                  </span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Ticker Speed Control */}
        <div className="hidden lg:flex items-center gap-3 px-6 border-s border-white/10 h-full group/speed">
           <span className="text-[9px] font-bold text-white/40 uppercase tracking-widest group-hover/speed:text-secondary transition-colors">{t('السرعة')}</span>
           <input 
              type="range" 
              min="10" 
              max="80" 
              step="5"
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              className="w-16 h-1 bg-white/10 rounded-full appearance-none cursor-pointer accent-secondary transition-opacity opacity-50 group-hover/speed:opacity-100"
              title={t("تعديل سرعة الشريط")}
           />
        </div>

        {/* Standard Social Icons - Hidden on very small screens */}
        <div className="hidden sm:flex items-center gap-1 border-s border-white/10 ps-6 h-full flex-shrink-0">
          {[
            { icon: <Facebook size={13} />, label: 'facebook' },
            { icon: <Twitter size={13} />, label: 'twitter' },
            { icon: <Youtube size={13} />, label: 'youtube' },
            { icon: <Instagram size={13} />, label: 'instagram' }
          ].map((social) => (
            <a
              key={social.label}
              href="#"
              className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-secondary transition-all"
            >
              {social.icon}
            </a>
          ))}
        </div>

      </div>
    </div>
  );
}
